import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { productService } from '@/services/product.service';
import { Product, ProductPack } from '@/types/product.types';
import { useCart } from '@/hooks/useCart';
import { useTestimonials } from '@/hooks/useTestimonials';
import { formatPrice } from '@/utils/formatters';
import '@/styles/product-detail.css';
import { useTranslation } from 'react-i18next';

/** L'API parse souvent additional_images en tableau JSON ; accepter aussi la chaîne brute SQL. */
function normalizeAdditionalImageUrls(raw: unknown): string[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.filter((u): u is string => typeof u === 'string' && u.length > 0);
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter((u): u is string => typeof u === 'string' && u.length > 0);
      }
    } catch {
      return [];
    }
  }
  return [];
}

function resolveProductPacks(product: Product | null): ProductPack[] | null {
  if (!product?.packs) return null;
  let list: unknown = product.packs;
  if (typeof list === 'string') {
    try {
      list = JSON.parse(list);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(list) || list.length === 0) return null;
  return list as ProductPack[];
}

function packBySelectedId(packs: ProductPack[], selectedPack: string | null): ProductPack | null {
  if (!selectedPack) return null;
  return packs.find((p) => String(p.id) === String(selectedPack)) ?? null;
}

/** Met à jour le panier local avec le bon prix (pack ou produit seul). */
function upsertProductCartLine(
  product: Product,
  quantity: number,
  selectedPackData: ProductPack | null
): void {
  const selectedUnitPrice = selectedPackData?.price ?? (product.reduced_price || product.price);
  const selectedProductName = selectedPackData
    ? `${product.name} - ${selectedPackData.duration}`
    : product.name;
  const cartData = localStorage.getItem('cart');
  let cart: Array<{
    id: number;
    name: string;
    price: number;
    image_url: string;
    quantity: number;
    selected_pack_id?: string;
    selected_pack_duration?: string;
  }> = [];
  if (cartData) {
    try {
      cart = JSON.parse(cartData);
    } catch {
      cart = [];
    }
  }
  const packLineId =
    selectedPackData?.id != null ? String(selectedPackData.id) : undefined;
  const existingItemIndex = cart.findIndex(
    (item) =>
      item.id === product.id &&
      String(item.selected_pack_id ?? '') === String(packLineId ?? '')
  );
  if (existingItemIndex >= 0) {
    cart[existingItemIndex].quantity = quantity;
  } else {
    cart.push({
      id: product.id,
      name: selectedProductName,
      price: selectedUnitPrice,
      image_url: product.image_url || '/images/bottle.png',
      quantity,
      selected_pack_id: packLineId,
      selected_pack_duration: selectedPackData?.duration,
    });
  }
  localStorage.setItem('cart', JSON.stringify(cart));
  window.dispatchEvent(new CustomEvent('cartUpdated', { detail: cart }));
}

export const ProductDetail = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const productId = searchParams.get('id');
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [selectedPack, setSelectedPack] = useState<string | null>(null);
  const { addToCart } = useCart();
  const { testimonials, loading: loadingTestimonials } = useTestimonials();
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [relatedLoading, setRelatedLoading] = useState(false);

  useEffect(() => {
    if (productId) {
      loadProduct(parseInt(productId));
    } else {
      setError(t('productDetailPage.notFound'));
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    const idNum = parseInt(productId, 10);
    if (Number.isNaN(idNum)) return;

    const loadRelated = async () => {
      try {
        setRelatedLoading(true);
        const data = await productService.getAll();
        const list = data
          .filter(
            (p) =>
              p.status === 'published' &&
              p.stock_status === 'in-stock' &&
              p.id !== idNum
          )
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          )
          .slice(0, 24);
        if (!cancelled) setRelatedProducts(list);
      } catch (e) {
        console.error('Erreur lors du chargement des produits associés:', e);
        if (!cancelled) setRelatedProducts([]);
      } finally {
        if (!cancelled) setRelatedLoading(false);
      }
    };

    void loadRelated();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  const loadProduct = async (id: number) => {
    try {
      setLoading(true);
      const data = await productService.getById(id);
      let normalized: Product = data;
      if (data.packs && typeof data.packs === 'string') {
        try {
          const parsed = JSON.parse(data.packs);
          normalized = { ...data, packs: Array.isArray(parsed) ? parsed : null };
        } catch {
          normalized = { ...data, packs: null };
        }
      }
      setProduct(normalized);
      const mainImg = normalized.image_url || '/images/bottle.png';
      setSelectedImage(mainImg);
    } catch (err) {
      console.error('Erreur lors du chargement du produit:', err);
      setError(t('productDetailPage.notFound'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!product) return;

    const packsList = resolveProductPacks(product);
    const packData =
      packsList && selectedPack ? packBySelectedId(packsList, selectedPack) : null;

    if (packData) {
      upsertProductCartLine(product, quantity, packData);
      alert(t('productDetailPage.alerts.addedToCart'));
      return;
    }

    try {
      const success = await addToCart(product.id, quantity);
      if (success) {
        alert(t('productDetailPage.alerts.addedToCart'));
      } else {
        alert(t('productDetailPage.alerts.addToCartError'));
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert(t('productDetailPage.alerts.addToCartError'));
    }
  };

  const handleBuyNow = async (e?: React.MouseEvent<HTMLElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!product || !isInStock) {
      alert(t('productDetailPage.alerts.notAvailable'));
      return;
    }
    
    try {
      const packsList = resolveProductPacks(product);
      const selectedPackData =
        packsList && selectedPack ? packBySelectedId(packsList, selectedPack) : null;

      if (selectedPackData) {
        upsertProductCartLine(product, quantity, selectedPackData);
        await new Promise((resolve) => setTimeout(resolve, 150));
        try {
          navigate('/checkout', { replace: false });
        } catch {
          window.location.href = '/checkout';
        }
        return;
      }

      const success = await addToCart(product.id, quantity);

      if (success) {
        await new Promise((resolve) => setTimeout(resolve, 150));
        try {
          navigate('/checkout', { replace: false });
        } catch {
          window.location.href = '/checkout';
        }
      } else {
        upsertProductCartLine(product, quantity, null);
        try {
          navigate('/checkout', { replace: false });
        } catch {
          window.location.href = '/checkout';
        }
      }
    } catch (error) {
      console.error('Erreur dans handleBuyNow:', error);
      try {
        const packsList = resolveProductPacks(product);
        const selectedPackData =
          packsList && selectedPack ? packBySelectedId(packsList, selectedPack) : null;
        upsertProductCartLine(product, quantity, selectedPackData);
        try {
          navigate('/checkout', { replace: false });
        } catch {
          window.location.href = '/checkout';
        }
      } catch (fallbackError) {
        alert(
          `Erreur lors de l'ajout au panier: ${error instanceof Error ? error.message : t('productDetailPage.alerts.unknownError')}`
        );
      }
    }
  };

  const getMaxQuantity = () => {
    if (!product) return 1;
    if (product.stock_unlimited) return 999;
    return product.stock_quantity || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div style={{ padding: '100px 20px', textAlign: 'center' }}>
          <div style={{ color: '#999' }}>{t('productDetailPage.loading')}</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div style={{ padding: '100px 20px', textAlign: 'center' }}>
          <h1 style={{ fontSize: '24px', marginBottom: '16px' }}>{t('productDetailPage.notFound')}</h1>
          <p style={{ color: '#666', marginBottom: '24px' }}>{error || t('productDetailPage.notFoundDescription')}</p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '12px 24px',
              background: '#8B204E',
              color: 'white',
              border: 'none',
              borderRadius: '9999px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 600
            }}
          >
            {t('productDetailPage.backHome')}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const baseProductPrice = product.reduced_price || product.price;
  const isInStock = product.stock_status === 'in-stock' && (product.stock_unlimited || (product.stock_quantity || 0) > 0);

  // Préparer les images pour la galerie (API = tableau ; ancienne donnée = chaîne JSON)
  const mainImage = product.image_url || '/images/bottle.png';
  const additionalImages = normalizeAdditionalImageUrls(product.additional_images);
  const allImages = [mainImage, ...additionalImages];

  // Utiliser les packs du produit uniquement s'ils existent
  const packs = resolveProductPacks(product);
  const selectedPackData =
    packs && selectedPack ? packBySelectedId(packs, selectedPack) : null;
  const displayPrice = selectedPackData ? selectedPackData.price : baseProductPrice;

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <div className="product-detail-container">
        {/* Breadcrumb */}
        <div className="product-breadcrumb">
          <span onClick={() => navigate('/')} className="breadcrumb-link">ACCUEIL</span>
          <span className="breadcrumb-separator"> &gt; </span>
          <span className="breadcrumb-link">{t('productDetailPage.productBreadcrumb')}</span>
          <span className="breadcrumb-separator"> &gt; </span>
          <span className="breadcrumb-current">{product.name.toUpperCase()}</span>
        </div>

        <div className="product-detail-content">
          {/* Images Section */}
          <div className="product-images-section">
            <div className="product-main-image-wrapper">
              <img
                src={selectedImage || mainImage}
                alt={product.name}
                className="product-main-image"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/images/bottle.png';
                }}
              />
            </div>
            
            {/* Miniatures */}
            {allImages.length > 1 && (
              <div className="product-thumbnails">
                {allImages.map((img, index) => {
                  const isActive = selectedImage === img || (!selectedImage && index === 0);
                  return (
                    <div
                      key={index}
                      className={`thumbnail-item ${isActive ? 'active' : ''}`}
                      onClick={() => setSelectedImage(img)}
                    >
                      <img
                        src={img}
                        alt={`${product.name} - ${t('productDetailPage.viewLabel', { index: index + 1 })}`}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/images/bottle.png';
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Product Info Section */}
          <div className="product-info-section">
            {/* Titre avec prix aligné à droite */}
            <div className="product-title-section">
              <div className="product-title-wrapper">
                <h1 className="product-title-main">{product.name}</h1>
              </div>
              <div className="product-price-stack">
                <div className="product-price-main">{formatPrice(displayPrice)}</div>
                {selectedPackData ? (
                  <p className="product-selected-pack-detail">
                    {t('productDetailPage.selectedPackPurchaseLine', {
                      treatment: t('productDetailPage.treatmentDuration', {
                        duration: selectedPackData.duration,
                      }),
                    })}
                  </p>
                ) : null}
              </div>
            </div>

            {/* Description */}
            {product.description && (
              <div className="product-description">
                <p>{product.description}</p>
              </div>
            )}

            {/* Section Pack - Afficher uniquement si le produit a des packs */}
            {packs && packs.length > 0 && (
              <div className="product-packs-section">
                <h3 className="packs-title">{t('productDetailPage.packTitle')}</h3>
                <div className="packs-grid">
                  {packs.map((pack) => (
                    <div
                      key={pack.id}
                      className={`pack-card ${selectedPack === String(pack.id) ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedPack(String(pack.id));
                      }}
                    >
                      {pack.badge && (
                        <div className={`pack-badge ${pack.badge === 'PLUS POPULAIRE' ? 'badge-popular' : 'badge-best'}`}>
                          {pack.badge}
                        </div>
                      )}
                      <div className="pack-content">
                        <div className="pack-duration">{t('productDetailPage.treatmentDuration', { duration: pack.duration })}</div>
                        <div className="pack-price">{t('productDetailPage.boxPrice', { price: formatPrice(pack.price) })}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity Selector */}
            <div className="quantity-selector-new">
              <label htmlFor="product-quantity" className="quantity-label">{t('productDetailPage.quantity')}</label>
              <select
                  id="product-quantity"
                  value={quantity}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 1;
                    const max = getMaxQuantity();
                    setQuantity(Math.max(1, Math.min(value, max)));
                  }}
                className="quantity-select"
              >
                {Array.from({ length: Math.min(getMaxQuantity(), 10) }, (_, i) => i + 1).map((num) => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

            {/* Action Buttons */}
            <div className="product-actions-new">
              <button
                className="btn-add-to-cart-primary-new"
                onClick={handleAddToCart}
                disabled={!isInStock}
              >
                {t('productDetailPage.addToCart')}
              </button>
              <a
                href="/checkout"
                className="btn-buy-now-link-new"
                onClick={(e) => {
                  e.preventDefault();
                  void handleBuyNow(e);
                }}
                aria-disabled={!isInStock}
              >
                {t('productDetailPage.buyNow')}
              </a>
            </div>
          </div>
        </div>

        {product.more_information?.trim() ? (
          <section
            className="product-detail-more-information-section"
            aria-labelledby="product-detail-more-info-title"
          >
            <h2 id="product-detail-more-info-title" className="gtitle">
              <span className="gtitle-light">
                {t('productDetailPage.moreInfoTitleLight')}
              </span>{' '}
              <span className="gtitle-strong">
                {t('productDetailPage.moreInfoTitleStrong')}
              </span>
            </h2>
            <div className="product-detail-more-information-body">
              {product.more_information.trim()}
            </div>
          </section>
        ) : null}

        {/* Nouveaux produits — défilement horizontal */}
        {(relatedLoading || relatedProducts.length > 0) && (
        <section
          className="product-detail-more-products-section"
          aria-labelledby="product-detail-more-products-title"
        >
          <h4 id="product-detail-more-products-title" className="gtitle">
            <span className="gtitle-light">
              {t('productDetailPage.moreProductsLight')}
            </span>{' '}
            <span className="gtitle-strong">
              {t('productDetailPage.moreProductsStrong')}
            </span>
          </h4>
          {relatedLoading ? (
            <p className="product-detail-more-products-empty">
              {t('productDetailPage.moreProductsLoading')}
            </p>
          ) : (
            <div className="product-detail-more-products-track">
              {relatedProducts.map((p) => {
                const cardPrice = p.reduced_price || p.price;
                const cardDiscount =
                  p.reduced_price != null && p.reduced_price < p.price;
                return (
                  <article
                    key={p.id}
                    className="product-detail-more-product-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/produit-detail?id=${p.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/produit-detail?id=${p.id}`);
                      }
                    }}
                  >
                    <div className="product-detail-more-product-image-wrap">
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt={p.name}
                          onError={(ev) => {
                            (ev.target as HTMLImageElement).src =
                              '/images/bottle.png';
                          }}
                        />
                      ) : (
                        <img
                          src="/images/bottle.png"
                          alt={t('boutiquePage.imageNotAvailable')}
                        />
                      )}
                    </div>
                    <h3 className="product-detail-more-product-name">
                      {p.name}
                    </h3>
                    <div className="product-detail-more-product-price-row">
                      <span className="product-detail-more-product-price">
                        {formatPrice(cardPrice)}
                      </span>
                      {cardDiscount && (
                        <span className="product-detail-more-product-price-old">
                          {formatPrice(p.price)}
                        </span>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
        )}

        <section
          className="home-testimonials-block product-detail-testimonials-block"
          aria-labelledby="product-detail-testimonials-title"
        >
          <h4 id="product-detail-testimonials-title" className="gtitle">
            <span className="gtitle-light">
              {t('homeLanding.testimonialsLight')}
            </span>{' '}
            <span className="gtitle-strong">
              {t('homeLanding.testimonialsStrong')}
            </span>
          </h4>
          <div className="home-testimonials-grid">
            {loadingTestimonials ? (
              <p className="home-faq-empty">
                {t('homeLanding.testimonialsLoading')}
              </p>
            ) : testimonials.length === 0 ? (
              <p className="home-faq-empty">{t('testimonials.empty')}</p>
            ) : (
              testimonials.slice(0, 6).map((testimonial) => (
                <article
                  key={testimonial.id}
                  className="home-testimonial-card"
                >
                  <div className="home-testimonial-header">
                    <div
                      className="home-testimonial-avatar"
                      aria-hidden="true"
                    >
                      {(
                        testimonial.customer_name ||
                        t('homeLanding.defaultClientLabel')
                      )
                        .charAt(0)
                        .toUpperCase()}
                    </div>
                    <div>
                      <h3>
                        {testimonial.customer_name ||
                          t('homeLanding.defaultClientLabel')}
                      </h3>
                      <p className="home-testimonial-rating">
                        {'★'.repeat(
                          Math.max(
                            1,
                            Math.min(5, testimonial.rating || 5)
                          )
                        )}
                      </p>
                    </div>
                  </div>
                  <p className="home-testimonial-comment">
                    {testimonial.comment}
                  </p>
                </article>
              ))
            )}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

